import { lazy, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { BrowserRouter } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import FusionCharts from "fusioncharts"
import { useLanguages } from "lang/languages"
import { inLocalDevelopment } from "modules/environments"
import { OverlayContext } from "modules/overlays"
import { useSite } from "modules/sites"
import { useAuth, UserContext } from "modules/users"
import { useScroll } from "utils/scroll"
import EntryMenu from "views/Layout/EntryMenu"
import Masthead from "views/Layout/Masthead"
import NotificationPanel from "views/Notification/NotificationPanel"
import DialogOverlay from "views/Overlay/DialogOverlay"
import LoadingOverlay from "views/Overlay/LoadingOverlay"

import styles from "views/Layout/Layout.module.scss"

FusionCharts.options.license({
  key: "<FE-11G5ramB13C6B5C3F2F1B4H4B3A8A3C3dB-11uC1D4C1A-21B1B4B1psfE4H1B9d1dA-13C4B4A3i1xxA5C5B3A5D1D4D1D3A4D4E2F-11wqG1H4DC11gapB4E4D4aG-7C8WE6F4hjH-9D2I3B6A8B6E5G5B1D3C3A1C8D7D1c==>",
  // key: 'wkB3gzpI2J3A4D9C4D4F6G5D3E1G2C11snnB4F1D4fH3C10D7A-13E-11iD3E2D2wqd1E3E2B2H-9moB1B11B1E5B3D3F3C2A33A18B14c1jjB4A2QD3jnA-21A5A1E3tgH2HC3A4B-16C-13nG2F4H1J3A6B4D5C4D3C2H4H2G2B8t==',
  creditLabel: false,
})

const AuthPage = lazy(() => import("views/AuthPage"))
const Sales = lazy(() => import("sites/Sales"))

const SuspensePlaceholder = () => {
  const { loadingRef } = useContext(OverlayContext)

  useEffect(() => loadingRef.current.open(), [loadingRef])

  return null
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (_, { status }) => {
        if (status === 401) {
          return false
        } else {
          return 3
        }
      },
    },
  },
})

const App = () => {
  const dialogRef = useRef()
  const loadingRef = useRef()
  const notificationPanelRef = useRef()
  const actionBarRef = useRef()
  const headBarRef = useRef()
  const scrollTopRef = useRef(0)
  const site = useSite()
  const { fetchUser, getAuthURL } = useAuth()
  const { language } = useLanguages()
  const { host, pathname } = window.location
  const [user, setUser] = useState(host.includes("mobile") ? null : undefined)

  const userContext = useMemo(() => ({ user, setUser }), [user])
  const overlayContext = useMemo(() => ({ loadingRef, dialogRef, actionBarRef }), [])

  const openNotificationPanel = useCallback(() => notificationPanelRef.current.open(), [])

  useEffect(() => {
    const finishCallback = loadingRef.current.open()
    fetchUser()
      .then(setUser)
      .catch((error) => {
        if (error?.status === 401) {
          setUser(null)
        } else {
          dialogRef.current.open({
            type: "error",
            message: {
              name: "帳號資訊取得失敗",
              title: "請稍候再試",
              content: error,
            },
          })
        }
      })
      .finally(finishCallback)
  }, [fetchUser, host, pathname, getAuthURL])

  useEffect(() => {
    if (!inLocalDevelopment && user === null) {
      getAuthURL()
        .then((url) => window.location.assign(url))
        .catch((error) =>
          dialogRef.current.open({
            type: "error",
            message: error,
          }),
        )
    }
  }, [user, getAuthURL, host])

  useEffect(() => {
    document.title = site.title
    document.querySelector('link[rel="apple-touch-icon"]').setAttribute("href", site.image)
    document.querySelector('link[rel="manifest"]').setAttribute("href", site.manifest)
    if (site.siteColor) {
      document.querySelector('meta[name="theme-color"]').setAttribute("content", site.siteColor)
      document.querySelector("#root").style.setProperty("--theme-color", site.siteColor)
      document.querySelector("#modal").style.setProperty("--theme-color", site.siteColor)
    }
  }, [site])

  useEffect(() => {
    document.documentElement.setAttribute("lang", language)
  }, [language])

  const scrollHeader = useCallback(({ scrollDiff, scrollTop, scrollCountDown }) => {
    if (scrollDiff > 0 && scrollTop > 50) {
      headBarRef.current?.hide()
      if (scrollCountDown < actionBarRef.current?.getHeight() + 50) {
        actionBarRef.current?.reveal({ nearBottom: true })
      } else {
        actionBarRef.current?.hide()
      }
    }
    if (scrollDiff < 0) {
      headBarRef.current?.reveal()
      actionBarRef.current?.reveal()
    }
    scrollTopRef.current = scrollTop
  }, [])

  const { handleScroll } = useScroll(scrollHeader)

  return (
    <QueryClientProvider client={queryClient}>
      <OverlayContext.Provider value={overlayContext}>
        <DialogOverlay ref={dialogRef} />
        <LoadingOverlay ref={loadingRef} />
        {user === undefined ? null : (
          <UserContext.Provider value={userContext}>
            <Suspense fallback={<SuspensePlaceholder />}>
              {user ? (
                <BrowserRouter>
                  <div className={styles.layout}>
                    <Masthead className={styles.header} handleRef={headBarRef} scrollTopRef={scrollTopRef} />
                    <EntryMenu className={styles.menu} openNotificationPanel={openNotificationPanel} />
                    <Suspense fallback={<SuspensePlaceholder />}>
                      <main className={styles.content} onScroll={handleScroll}>
                        <Sales />
                      </main>
                    </Suspense>
                  </div>
                  <NotificationPanel ref={notificationPanelRef} />
                </BrowserRouter>
              ) : inLocalDevelopment ? (
                <AuthPage />
              ) : null}
            </Suspense>
          </UserContext.Provider>
        )}
      </OverlayContext.Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
